import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import {
    Ordering,
    VisitDatetimeField,
    VisitOutcome,
    VisitRequestState,
    VisitState,
} from '@/types/graphql';
import moment from 'moment';

import {
    ContainerDefinitionType,
    DEFAULT_CONTAINER_FILTER_WINDOW_DAYS,
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeVisitRequestsRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitRequestsRowToLink,
    makeContainerDefinition,
} from '../definitions';
import {
    EpisodeOfCareDashboardContainer,
    PatientDashboardContainer,
    RootEntity,
    VisitDashboardContainer,
    VisitRequestDashboardContainer,
} from '../types';
import { HOSPICE_DEMO_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { IDashboardVariationConfig } from './types';

/**
 * Demo-only filters: same logic as Northstar but keyed by service name instead of external ID,
 * for use in hospice demo where external IDs may not be present.
 *
 * Org service names: Admission Visit; HA Death Visit, MSW Death Visit, SC Death Visit, SN Death Visit;
 * HOPE Update Visit HUV1, HOPE Update Visit HUV2; MSW Eval, SC Eval, SN Eval Visit;
 * SN Recertification Visit; Symptom Follow-Up Visit; etc.
 */

const DEMO_ADMIT_VISIT_SERVICE_FILTER = {
    name: { iContains: 'Admission Visit' },
};

const DEMO_IS_ACTIVE_BENEFIT_PERIOD_FILTER = {
    isActiveByDate: { exact: true },
    isActiveField: { exact: true },
};

const DEMO_ADMITTED_BENEFIT_PERIOD_FILTER = {
    ...DEMO_IS_ACTIVE_BENEFIT_PERIOD_FILTER,
    visits: {
        services: DEMO_ADMIT_VISIT_SERVICE_FILTER,
        outcome: { exact: VisitOutcome.COMPLETED },
    },
};

const DEMO_ADMITTED_BENEFIT_PERIOD_1_FILTER = {
    ...DEMO_ADMITTED_BENEFIT_PERIOD_FILTER,
    numberInSequence: { exact: 1 },
};

const DEMO_EVAL_VISIT_REQUESTS_CONTAINER: VisitRequestDashboardContainer = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.evalVisitRequests,
    label: 'Evals',
    defaultEnabled: true,
    hoverText: 'Eval visit requests that need scheduling.',
    tableConfig: {
        entity: RootEntity.VISIT_REQUEST,
        filters: {
            services: { name: { iContains: 'eval' } },
            state: { exact: VisitRequestState.PENDING },
        },
        rowToLink: defaultEpisodeVisitRequestsRowToLink,
        ordering: { patient: { fullName: Ordering.ASC } },
    },
};

/** Admissions visits: visits for admission services (demo uses service name). */
const DEMO_ADMISSIONS_VISITS_CONTAINER: VisitDashboardContainer = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.admissionsVisits,
    label: 'Admissions visits',
    defaultEnabled: true,
    hoverText: 'Upcoming admission visits that need scheduling.',
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            patient: { isActive: { exact: true } },
            services: DEMO_ADMIT_VISIT_SERVICE_FILTER,
            state: { exact: VisitState.UNSCHEDULED },
        },
        ordering: { fulfillmentWindowEndDate: Ordering.ASC },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No admission visits to schedule.',
    },
};

/** Incomplete care teams: patients with completed admit but visit disciplines not covered by care team. */
const DEMO_INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.incompleteCareTeam,
    label: 'Incomplete care teams',
    defaultEnabled: true,
    hoverText: 'Patients that need care teams assigned.',
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            episodes: {
                ...DEMO_IS_ACTIVE_BENEFIT_PERIOD_FILTER,
                visits: {
                    services: DEMO_ADMIT_VISIT_SERVICE_FILTER,
                    outcome: { exact: VisitOutcome.COMPLETED },
                },
            },
            AND: {
                episodes: {
                    ...DEMO_IS_ACTIVE_BENEFIT_PERIOD_FILTER,
                    visits: {
                        disciplineCoveredByCareTeam: { exact: false },
                        outcome: { exact: VisitOutcome.NONE },
                    },
                },
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients with incomplete care teams.',
    },
};

/** Patients needing scheduling: episodes with pending visit requests (demo uses service-name deceased filter). */
const DEMO_PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients needing scheduling',
    defaultEnabled: true,
    hoverText: 'Patients that need scheduling.',
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            patient: { isActive: { exact: true } },
            endDate: { gte: GraphInputHelpers.date(moment().startOf('day').subtract(14, 'days')) },
            visitRequests: { state: { exact: VisitRequestState.PENDING } },
        },
        ordering: { startDate: Ordering.ASC },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No patients to schedule.',
    },
};

/** Expiring fulfillment windows (demo uses service-name deceased filter). */
const DEMO_EXPIRING_FULFILLMENT_WINDOW_CONTAINER: VisitRequestDashboardContainer = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.expiringFulfillmentWindow,
    label: 'Expiring Fulfillment Windows',
    defaultEnabled: true,
    hoverText: 'Visits with expiring fulfillment windows.',
    tableConfig: {
        entity: RootEntity.VISIT_REQUEST,
        filters: {
            patient: { isActive: { exact: true } },
            state: { exact: VisitRequestState.PENDING },
            fulfillmentWindowEndDate: {
                gte: GraphInputHelpers.date(moment().subtract(31, 'days')),
                lte: GraphInputHelpers.date(moment().add(3, 'days')),
            },
        },
        ordering: { fulfillmentWindowEndDate: Ordering.ASC },
        rowToLink: defaultVisitRequestsRowToLink,
        emptyTableText: 'No visits with expiring fulfillment windows.',
    },
};

/** Patient no-shows (demo uses service-name deceased filter). */
const DEMO_PATIENT_NO_SHOWS_CONTAINER: VisitDashboardContainer = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.patientNoShows,
    label: 'Patient No-Shows',
    defaultEnabled: true,
    hoverText: 'Patient no-shows requiring attention',
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        emptyTableText: 'No patient no-shows requiring attention.',
        filters: {
            patient: { isActive: { exact: true } },
            state: { exact: VisitState.IN_REVIEW },
            outcome: { exact: VisitOutcome.PATIENT_DID_NOT_SHOW },
        },
        ordering: { startDatetime: Ordering.DESC },
        rowToLink: ({ id }: { id: string }) => `/visits/${id}`,
    },
};

/** Recerts: episodes ending soon with no scheduled/completed recert (demo uses service names). */
const DEMO_RECERT_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.recert,
    label: 'Recerts',
    defaultEnabled: true,
    hoverText: 'Upcoming Recerts that need scheduling.',
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        emptyTableText: 'No Recerts to schedule.',
        filters: {
            patient: { isActive: { exact: true } },
            endDate: {
                range: {
                    start: GraphInputHelpers.date(new Date()),
                    end: GraphInputHelpers.date(
                        moment().add(DEFAULT_CONTAINER_FILTER_WINDOW_DAYS, 'days'),
                    ),
                },
            },
            AND: {
                ...DEMO_ADMITTED_BENEFIT_PERIOD_1_FILTER,
                OR: {
                    ...DEMO_IS_ACTIVE_BENEFIT_PERIOD_FILTER,
                    numberInSequence: { gt: 1 },
                },
            },
            visits: {
                NOT: {
                    services: { name: { iContains: 'Recertification Visit' } },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                },
            },
        },
        ordering: { patient: { fullName: Ordering.ASC } },
        rowToLink: defaultEpisodeOfCareRowToLink,
    },
};

const DEMO_HOSPICE_HUV_CONTAINER: PatientDashboardContainer = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.huv,
    label: 'HUVs',
    defaultEnabled: true,
    hoverText: 'Upcoming HUVs that need scheduling.',
    tableConfig: {
        entity: RootEntity.PATIENT,
        emptyTableText: 'No HUVs to schedule.',
        filters: {
            // HUV1: Day 6-15 window (episode started 5-14 days ago)
            episodes: {
                id: { isNull: false },
                isActiveByDate: { exact: true },
                startDate: {
                    range: {
                        start: GraphInputHelpers.date(moment().subtract(14, 'days')),
                        end: GraphInputHelpers.date(moment().subtract(5, 'days')),
                    },
                },
                NOT: {
                    visits: {
                        services: { name: { iContains: 'HUV1' } },
                    },
                },
            },
            OR: {
                // HUV2: Day 16-30 window (episode started 15-29 days ago)
                episodes: {
                    id: { isNull: false },
                    isActiveByDate: { exact: true },
                    startDate: {
                        range: {
                            start: GraphInputHelpers.date(moment().subtract(29, 'days')),
                            end: GraphInputHelpers.date(moment().subtract(15, 'days')),
                        },
                    },
                    NOT: {
                        visits: {
                            services: { name: { iContains: 'HUV2' } },
                        },
                    },
                },
            },
        },
        ordering: { fullName: Ordering.ASC },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
    },
};

const DEMO_HOSPICE_SFV_CONTAINER: VisitDashboardContainer = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.sfv,
    label: 'SFVs',
    defaultEnabled: true,
    hoverText: 'Follow-up visits required within 2 days of Symptom Impact Assessments.',
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        emptyTableText: 'No SFVs need attention at this time.',
        filters: {
            metadata: { hasKey: 'sfv_required' },
            relatedVisitCondition: {
                exists: false,
                filter: {
                    services: { name: { iContains: 'Symptom Follow-Up Visit' } },
                    outcome: { inList: [VisitOutcome.COMPLETED, VisitOutcome.NONE] },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.TENTATIVE_DATE,
                        gteOffsetMinutes: 0,
                        lteOffsetMinutes: 2880, // 48 hours
                    },
                ],
                samePatient: true,
                sameEpisode: true,
            },
        },
        rowToLink: ({ patient }: { patient: { id: string } }) =>
            `/patients/${patient.id}/scheduling`,
        ordering: { startDatetime: Ordering.ASC },
    },
    extraProps: { showClinicianColumn: false },
};

export const HOSPICE_DEMO_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        DEMO_EVAL_VISIT_REQUESTS_CONTAINER,
        DEMO_ADMISSIONS_VISITS_CONTAINER,
        DEMO_HOSPICE_HUV_CONTAINER,
        DEMO_INCOMPLETE_CARE_TEAM_CONTAINER,
        DEMO_PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        makeContainerDefinition({
            type: ContainerDefinitionType.UNASSIGNED,
            key: HOSPICE_DEMO_CONTAINER_KEYS.unassigned,
            defaultEnabled: true,
            overrides: { tableConfig: { highlightLinkedVisit: true } },
        }),
        DEMO_HOSPICE_SFV_CONTAINER,
        DEMO_EXPIRING_FULFILLMENT_WINDOW_CONTAINER,
        DEMO_PATIENT_NO_SHOWS_CONTAINER,
        DEMO_RECERT_CONTAINER,
    ],
};
